import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { resolveImageUrl } from '@/utils/image';
import InlineEnquiryForm from '@/components/InlineEnquiryForm';

const SiteFooter = lazy(() => import('@/components/SiteFooter'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_ADJ = { opacity: 70, brightness: 0, contrast: 0 };
const ANIMATION_OFFSET_Y = 30;
const STAGGER_DELAY = 0.1;
const REVIEW_STAGGER = 0.1;
const SECTION_MARGIN = '-50px';

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  transition: { duration: 0.8 },
  viewport: { once: true, margin: SECTION_MARGIN },
};

const heroReveal = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: 'easeOut' },
};

const LazyImage = ({ src, alt, className }) => (
  <img src={resolveImageUrl(src)} alt={alt} className={className} loading="lazy" decoding="async" />
);

const ServiceCard = ({ service, index }) => {
  const cardAnim = useMemo(() => ({
    initial: { opacity: 0, y: ANIMATION_OFFSET_Y },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay: index * STAGGER_DELAY },
    viewport: { once: true, margin: '-80px' },
  }), [index]);

  return (
    <motion.div
      className="relative overflow-hidden group h-[60vh] border border-white/10 bg-neutral-900"
      {...cardAnim}
      data-testid={`service-card-${index}`}
    >
      <div className="absolute inset-0">
        <LazyImage src={service.image_url} alt={service.title} className="w-full h-full object-cover grayscale-hover" />
        <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-all duration-700" />
      </div>
      <div className="relative z-10 h-full flex flex-col justify-end p-8">
        <h3 className="text-2xl md:text-3xl font-normal mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{service.title}</h3>
        <p className="text-sm text-gray-300 tracking-wide">{service.description}</p>
      </div>
    </motion.div>
  );
};

const ReviewCard = ({ review, index }) => {
  const anim = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5, delay: index * REVIEW_STAGGER },
    viewport: { once: true, margin: SECTION_MARGIN },
  }), [index]);

  return (
    <motion.div className="border border-white/10 p-8" {...anim} data-testid={`review-card-${index}`}>
      <p className="text-gray-300 leading-relaxed mb-6 text-sm italic">"{review.text}"</p>
      <p className="text-white text-sm font-medium tracking-wide">— {review.name}</p>
    </motion.div>
  );
};

const ServicesContent = ({ loading, services }) => {
  if (loading) return <div className="text-center py-20"><p className="text-gray-400">Loading services...</p></div>;
  if (services.length === 0) return <div className="text-center py-20"><p className="text-gray-400">No services available</p></div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      {services.map((service, index) => <ServiceCard key={service.id} service={service} index={index} />)}
    </div>
  );
};

const GalleryContent = ({ reviews }) => {
  if (reviews.length === 0) return <p className="text-gray-400 text-sm tracking-wide">No reviews yet.</p>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      {reviews.map((review, index) => <ReviewCard key={review.id} review={review} index={index} />)}
    </div>
  );
};

const Home = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aboutText, setAboutText] = useState('');
  const [heroImage, setHeroImage] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [heroAdj, setHeroAdj] = useState(DEFAULT_HERO_ADJ);
  const [reviews, setReviews] = useState([]);

  const fetchServices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Services fetch failed:', err); }
    finally { setLoading(false); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAboutText(res.data.about_text || '');
      setHeroImage(res.data.hero_image || '');
      setLogoUrl(res.data.logo_url || '');
      if (res.data.hero_adjustments) setHeroAdj(res.data.hero_adjustments);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Settings fetch failed:', err); }
  }, []);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error('Reviews fetch failed:', err); }
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const heroStyle = useMemo(() => ({
    backgroundImage: heroImage ? `url(${resolveImageUrl(heroImage)})` : 'none',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: heroAdj.opacity / 100,
    filter: `brightness(${1 + heroAdj.brightness / 100}) contrast(${1 + heroAdj.contrast / 100})`,
  }), [heroImage, heroAdj]);

  return (
    <div>
      <section className="relative h-screen w-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full" style={heroStyle} />
        <div className="absolute inset-0 hero-overlay" />
        <motion.div className="relative z-10 text-center px-6" {...heroReveal}>
          {logoUrl && (
            <div className="mb-10">
              <img src={resolveImageUrl(logoUrl)} alt="Jay Captures Photography" className="mx-auto max-w-lg w-full px-4" data-testid="home-logo" loading="eager" />
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/gallery" className="inline-block bg-white text-black hover:bg-gray-200 rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300" data-testid="view-work-button">
              View Work
            </Link>
            <Link to="/contact" className="inline-block border border-white/40 text-white hover:bg-white hover:text-black rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300" data-testid="hero-enquire-button">
              Enquire Now
            </Link>
          </div>
        </motion.div>
      </section>

      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <motion.div {...fadeIn}>
          <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>Services</h2>
          <ServicesContent loading={loading} services={services} />
          <div className="mt-12 text-center">
            <Link to="/contact" className="inline-block border border-white/20 text-white hover:bg-white hover:text-black rounded-none px-8 py-4 text-sm uppercase tracking-widest transition-all duration-300" data-testid="services-enquire-button">
              Enquire About a Shoot
            </Link>
          </div>
        </motion.div>
      </section>

      {aboutText && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-white/10">
          <motion.div {...fadeIn}>
            <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-12" style={{ fontFamily: 'Playfair Display, serif' }}>About</h2>
            <p className="text-base md:text-lg text-gray-300 leading-relaxed max-w-3xl whitespace-pre-line" data-testid="about-text">{aboutText}</p>
          </motion.div>
        </section>
      )}

      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-white/10" data-testid="reviews-section">
        <motion.div {...fadeIn}>
          <h2 className="text-4xl md:text-6xl font-normal tracking-tight mb-16" style={{ fontFamily: 'Playfair Display, serif' }}>Reviews</h2>
          <GalleryContent reviews={reviews} />
        </motion.div>
      </section>

      <InlineEnquiryForm heading="Get In Touch" subheading="Interested in working together? Fill out the form below and I'll get back to you." />

      <Suspense fallback={null}><SiteFooter /></Suspense>
    </div>
  );
};

export default Home;
